<template>
  <div>
    <!-- Thanh điều khiển -->
    <div class="d-flex align-items-center mb-3 flex-wrap">
      <span class="text-muted mr-2">Khoảng:</span>
      <select v-model="range" class="form-control form-control-sm" style="width:auto" @change="loadAll">
        <option value="24h">24 giờ qua</option>
        <option value="7d">7 ngày qua</option>
        <option value="30d">30 ngày qua</option>
        <option value="90d">90 ngày qua</option>
      </select>
      <button class="btn btn-sm btn-outline-secondary ml-2" :disabled="loading" @click="loadAll">
        <i :class="loading ? 'fa fa-spinner fa-spin' : 'fa fa-refresh'"></i> Tải lại
      </button>
      <span v-if="lastUpdatedStr" class="text-muted small ml-3"><i class="fa fa-clock-o"></i> {{ lastUpdatedStr }} <span class="text-success">(tự động 60s)</span></span>
    </div>

    <!-- Alert active -->
    <div v-if="activeAlerts.length" class="alert alert-warning py-2">
      <b><i class="fa fa-exclamation-triangle"></i> {{ activeAlerts.length }} cảnh báo chưa xử lý</b>
      <ul class="mb-0 mt-1 small">
        <li v-for="a in activeAlerts.slice(0, 5)" :key="a._id">
          <span class="badge" :class="a.alert.severity === 'critical' ? 'badge-danger' : 'badge-warning'">{{ a.alert.alertType }}</span>
          {{ alertText(a) }}
          <a href="#" class="ml-2" @click.prevent="resolveAlert(a)">Đánh dấu đã xử lý</a>
        </li>
      </ul>
    </div>

    <!-- Thẻ tổng quan cost -->
    <div class="row mb-3" v-if="summary.latestCost">
      <div class="col-md-3"><div class="card stat-card"><div class="card-body text-center">
        <div class="stat-label">Tier1 (PUT/LIST) — {{ summary.latestCost.date }}</div>
        <div class="stat-value" :class="summary.latestCost.cost.tier1Cost > 100 ? 'text-danger' : ''">${{ fmtMoney(summary.latestCost.cost.tier1Cost) }}</div>
        <div class="text-muted small">{{ fmtNum(summary.latestCost.cost.tier1Requests) }} request</div>
      </div></div></div>
      <div class="col-md-3"><div class="card stat-card"><div class="card-body text-center">
        <div class="stat-label">Tier2 (GET)</div>
        <div class="stat-value">${{ fmtMoney(summary.latestCost.cost.tier2Cost) }}</div>
        <div class="text-muted small">{{ fmtNum(summary.latestCost.cost.tier2Requests) }} request</div>
      </div></div></div>
      <div class="col-md-3"><div class="card stat-card"><div class="card-body text-center">
        <div class="stat-label">Lưu trữ</div>
        <div class="stat-value">${{ fmtMoney(summary.latestCost.cost.storageCost) }}</div>
      </div></div></div>
      <div class="col-md-3"><div class="card stat-card"><div class="card-body text-center">
        <div class="stat-label">Tổng ngày</div>
        <div class="stat-value">${{ fmtMoney(summary.latestCost.cost.totalCost) }}</div>
        <div class="text-muted small">Egress ${{ fmtMoney(summary.latestCost.cost.egressCost) }}</div>
      </div></div></div>
    </div>

    <!-- KHỐI 2: Request theo prefix (chính) -->
    <div class="card mb-3">
      <div class="card-header d-flex align-items-center">
        <span><i class="fa fa-sitemap text-primary"></i> Request theo prefix — "cái gì đang bị call nhiều"</span>
        <div class="ml-auto btn-group btn-group-sm">
          <button v-for="m in metricTypes" :key="m.key" class="btn" :class="metricType === m.key ? 'btn-primary' : 'btn-outline-primary is-border-1'" @click="metricType = m.key">{{ m.label }}</button>
        </div>
      </div>
      <div class="card-body">
        <apexchart v-if="requestSeries.length" type="area" height="300" :series="requestSeries" :options="requestChartOptions"></apexchart>
        <div v-else class="text-center text-muted py-4">
          <i class="fa fa-inbox fa-2x d-block mb-2"></i>
          Chưa có dữ liệu request metrics. Cần chạy <code>node bin/s3-metrics-setup apply</code> để bật CloudWatch Request Metrics (thu sau ~15 phút, không hồi tố).
        </div>
      </div>
    </div>

    <!-- KHỐI 1: Archive runs (bin/lake) -->
    <div class="card mb-3">
      <div class="card-header"><i class="fa fa-database text-primary"></i> Lịch sử archive (bin/lake) — "job nào bơm bao nhiêu record"</div>
      <div class="table-responsive">
        <table class="table table-hover table-sm mb-0 align-middle">
          <thead class="thead-light"><tr>
            <th>Thời gian</th><th>Lệnh</th><th>Nguồn</th><th>Client</th>
            <th class="text-right">Record</th><th class="text-right">Batch</th><th class="text-right">Thời lượng</th><th>Trạng thái</th>
          </tr></thead>
          <tbody>
            <tr v-for="r in archiveRuns" :key="r._id">
              <td class="text-nowrap">{{ fmtTime(r.createTime) }}</td>
              <td><code>{{ r.command || '—' }}</code></td>
              <td>{{ r.triggeredBy === 'cron' ? 'Cron' : 'Thủ công' }}<small v-if="r.triggeredUser" class="text-muted"> · {{ r.triggeredUser }}</small></td>
              <td>{{ r.client || 'Toàn bộ' }}</td>
              <td class="text-right font-weight-bold">{{ fmtNum(r.recordCount) }}</td>
              <td class="text-right">{{ fmtNum(r.batchCount) }}</td>
              <td class="text-right">{{ fmtDur(r.durationMs) }}</td>
              <td>
                <span class="badge" :class="statusBadge(r.status)">{{ statusWord(r.status) }}</span>
                <i v-if="r.error" class="fa fa-info-circle text-danger ml-1" :title="r.error"></i>
              </td>
            </tr>
            <tr v-if="!archiveRuns.length"><td colspan="8" class="text-center text-muted py-4">Chưa có lần archive nào trong khoảng này</td></tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- KHỐI ĐỌC LAKE: đo "cái gì đọc lake gây LIST/Tier1" (nghi vấn inbox mở khách cũ) -->
    <div class="card mb-3">
      <div class="card-header"><i class="fa fa-search text-primary"></i> Đọc data lake (Athena) — "cái gì đang query lake"</div>
      <div class="card-body">
        <div v-if="!lakeSummary.bySource.length" class="text-center text-muted py-4">
          <i class="fa fa-inbox fa-2x d-block mb-2"></i>Chưa ghi nhận lượt đọc lake nào trong khoảng này
        </div>
        <div v-else class="row">
          <div class="col-md-6">
            <div class="text-muted small mb-1">Theo nguồn gọi</div>
            <table class="table table-sm mb-0">
              <thead class="thead-light"><tr><th>Nguồn</th><th class="text-right">Lượt</th><th class="text-right">Quét (MB)</th><th class="text-right">Athena $</th><th class="text-right">TB avg</th></tr></thead>
              <tbody>
                <tr v-for="s in lakeSummary.bySource" :key="s._id" :class="s._id === 'customer-timeline' ? 'table-warning' : ''">
                  <td><code>{{ s._id || 'unknown' }}</code><i v-if="s._id === 'customer-timeline'" class="fa fa-comment ml-1 text-warning" title="inbox mở khách cũ"></i></td>
                  <td class="text-right font-weight-bold">{{ fmtNum(s.count) }}</td>
                  <td class="text-right">{{ fmtNum(Math.round(s.scannedMB)) }}</td>
                  <td class="text-right">${{ fmtMoney(s.costUsd) }}</td>
                  <td class="text-right">{{ Math.round(s.avgMs) }}ms</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="col-md-6">
            <div class="text-muted small mb-1">Top khách bị mở timeline → chạm lake</div>
            <table class="table table-sm mb-0">
              <thead class="thead-light"><tr><th>Customer</th><th class="text-right">Lượt query</th><th class="text-right">Quét (MB)</th></tr></thead>
              <tbody>
                <tr v-for="(c, i) in lakeSummary.topCustomers.slice(0, 10)" :key="i">
                  <td><small>{{ c._id.customer || '—' }}</small></td>
                  <td class="text-right">{{ c.count }}</td>
                  <td class="text-right">{{ fmtNum(Math.round(c.scannedMB)) }}</td>
                </tr>
                <tr v-if="!lakeSummary.topCustomers.length"><td colspan="3" class="text-center text-muted">Không có lượt đọc customer-timeline</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- KHỐI 3: Cost tổng -->
    <div class="card mb-3">
      <div class="card-header"><i class="fa fa-dollar text-primary"></i> Chi phí S3 theo ngày (Cost Explorer)</div>
      <div class="card-body">
        <apexchart v-if="costSeries.length" type="line" height="280" :series="costSeries" :options="costChartOptions"></apexchart>
        <div v-else class="text-center text-muted py-4"><i class="fa fa-inbox fa-2x d-block mb-2"></i>Chưa có snapshot chi phí (worker chạy 07:20 hằng ngày cho ngày hôm trước)</div>
      </div>
    </div>

    <!-- Guard: bộ đếm call AWS worker -->
    <div class="card mb-3" v-if="monitorStat.length">
      <div class="card-header diag-legend-toggle" @click="showGuard = !showGuard">
        <i class="fa fa-shield text-info"></i> Guard — số call AWS worker (chống loop tốn tiền)
        <i class="fa float-right mt-1" :class="showGuard ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
      </div>
      <div class="table-responsive" v-show="showGuard">
        <table class="table table-sm mb-0">
          <thead class="thead-light"><tr><th>Ngày</th><th class="text-right">Cost Explorer</th><th class="text-right">CloudWatch</th><th class="text-right">Bị chặn</th></tr></thead>
          <tbody>
            <tr v-for="s in monitorStat" :key="s.date">
              <td>{{ s.date }}</td>
              <td class="text-right">{{ (s.monitorStat && s.monitorStat.ceCalls) || 0 }}</td>
              <td class="text-right">{{ (s.monitorStat && s.monitorStat.cwCalls) || 0 }}</td>
              <td class="text-right" :class="(s.monitorStat && s.monitorStat.blocked) ? 'text-danger font-weight-bold' : 'text-muted'">{{ (s.monitorStat && s.monitorStat.blocked) || 0 }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';

export default {
  name: 'S3CostTab',
  components: { 'apexchart': VueApexCharts },
  data() {
    return {
      range: '7d',
      loading: false,
      lastUpdatedStr: '',
      summary: { latestCost: null, topPrefixes: [], activeAlerts: 0 },
      costDocs: [],
      requestDocs: [],
      archiveRuns: [],
      activeAlerts: [],
      monitorStat: [],
      lakeSummary: { bySource: [], topCustomers: [] },
      metricType: 'putRequests',
      metricTypes: [
        { key: 'putRequests', label: 'PUT' },
        { key: 'listRequests', label: 'LIST' },
        { key: 'getRequests', label: 'GET' },
        { key: 'allRequests', label: 'Tất cả' },
      ],
      showGuard: false,
    };
  },
  computed: {
    // Chart request: 1 series/prefix, giá trị theo metricType đang chọn.
    requestSeries() {
      const byPrefix = {};
      for (const d of this.requestDocs) {
        const m = d.metric || {};
        const key = `${m.bucket}/${m.prefix}`;
        if (!byPrefix[key]) byPrefix[key] = [];
        byPrefix[key].push([d.periodStart, m[this.metricType] || 0]);
      }
      return Object.keys(byPrefix).map(k => ({ name: k, data: byPrefix[k] }));
    },
    requestChartOptions() {
      return {
        chart: { id: 's3-requests', stacked: true, toolbar: { show: false }, animations: { enabled: false } },
        dataLabels: { enabled: false },
        xaxis: { type: 'datetime', labels: { datetimeUTC: false } },
        yaxis: { labels: { formatter: v => this.fmtNum(Math.round(v)) } },
        stroke: { width: 1, curve: 'straight' },
        fill: { type: 'solid', opacity: 0.55 },
        legend: { position: 'top' },
        tooltip: { x: { format: 'HH:00 dd/MM' }, y: { formatter: v => this.fmtNum(Math.round(v)) + ' request' } },
      };
    },
    costSeries() {
      if (!this.costDocs.length) return [];
      const tier1 = [], tier2 = [], storage = [];
      for (const d of this.costDocs) {
        const c = d.cost || {};
        tier1.push([d.periodStart, c.tier1Cost || 0]);
        tier2.push([d.periodStart, c.tier2Cost || 0]);
        storage.push([d.periodStart, c.storageCost || 0]);
      }
      return [
        { name: 'Tier1 (PUT/LIST)', data: tier1 },
        { name: 'Tier2 (GET)', data: tier2 },
        { name: 'Lưu trữ', data: storage },
      ];
    },
    costChartOptions() {
      return {
        chart: { id: 's3-cost', toolbar: { show: false }, animations: { enabled: false } },
        xaxis: { type: 'datetime', labels: { datetimeUTC: false } },
        yaxis: { labels: { formatter: v => '$' + (Math.round(v * 100) / 100) } },
        stroke: { width: 2, curve: 'straight' },
        legend: { position: 'top' },
        tooltip: { x: { format: 'dd/MM' }, y: { formatter: v => '$' + (Math.round(v * 100) / 100) } },
      };
    },
  },
  mounted() {
    this.loadAll();
    this._timer = setInterval(() => this.loadAll(true), 60000);
  },
  beforeDestroy() {
    if (this._timer) { clearInterval(this._timer); this._timer = null; }
  },
  methods: {
    async loadAll(silent) {
      if (!silent) this.loading = true;
      try {
        const [sm, cost, reqs, runs, alerts, stat, lake] = await Promise.all([
          this.$service.get('/s3-cost/summary'),
          this.$service.get('/s3-cost/cost', { params: { range: this.range } }),
          this.$service.get('/s3-cost/requests', { params: { range: this.range } }),
          this.$service.get('/s3-cost/archive-runs', { params: { range: this.range, limit: 100 } }),
          this.$service.get('/s3-cost/alerts', { params: { range: this.range, active: 1 } }),
          this.$service.get('/s3-cost/monitor-stat', { params: { range: this.range } }),
          this.$service.get('/s3-cost/lake-summary', { params: { range: this.range } }),
        ]);
        this.summary = sm.data || this.summary;
        this.costDocs = (cost.data && cost.data.docs) || [];
        this.requestDocs = (reqs.data && reqs.data.docs) || [];
        this.archiveRuns = (runs.data && runs.data.docs) || [];
        this.activeAlerts = (alerts.data && alerts.data.alerts) || [];
        this.monitorStat = (stat.data && stat.data.docs) || [];
        this.lakeSummary = { bySource: (lake.data && lake.data.bySource) || [], topCustomers: (lake.data && lake.data.topCustomers) || [] };
        this.lastUpdatedStr = new Date().toLocaleTimeString('vi-VN');
      } catch (e) { /* giữ dữ liệu cũ nếu lỗi */ }
      finally { this.loading = false; }
    },
    async resolveAlert(a) {
      try {
        await this.$service.post(`/s3-cost/alerts/${a._id}/resolve`);
        this.activeAlerts = this.activeAlerts.filter(x => x._id !== a._id);
      } catch (e) { /* noop */ }
    },
    alertText(a) {
      const d = (a.alert && a.alert.detail) || {};
      if (a.alert.alertType === 'prefix-request-spike') return `${d.prefix}: ${this.fmtNum(d.putRequests)} PUT/giờ`;
      if (a.alert.alertType === 'tier1-cost-spike') return `${d.date}: Tier1 $${this.fmtMoney(d.tier1Cost)}`;
      if (a.alert.alertType === 'monitor-runaway') return `Worker gọi ${d.kind} vượt ${d.cap}/ngày — đã chặn`;
      return JSON.stringify(d).slice(0, 120);
    },
    fmtMoney(v) { return (Math.round((v || 0) * 100) / 100).toFixed(2); },
    fmtNum(v) {
      v = v || 0;
      if (v >= 1000000) return (v / 1000000).toFixed(1) + 'M';
      if (v >= 1000) return (v / 1000).toFixed(1) + 'K';
      return String(v);
    },
    fmtTime(ms) { try { return new Date(ms).toLocaleString('vi-VN'); } catch (e) { return ms; } },
    fmtDur(ms) {
      if (!ms) return '—';
      if (ms < 1000) return ms + 'ms';
      if (ms < 60000) return (ms / 1000).toFixed(1) + 's';
      return Math.round(ms / 60000) + 'p';
    },
    statusBadge(s) { return { done: 'badge-success', running: 'badge-info', failed: 'badge-danger' }[s] || 'badge-light'; },
    statusWord(s) { return { done: 'Xong', running: 'Đang chạy', failed: 'Lỗi' }[s] || s; },
  },
};
</script>

<style scoped>
.stat-card { border: none; box-shadow: 0 1px 3px rgba(0, 0, 0, .08); border-radius: .5rem; }
.stat-card .stat-value { font-size: 1.5rem; font-weight: 700; line-height: 1.2; }
.stat-card .stat-label { font-size: .78rem; color: #6c757d; }
.card-header { font-weight: 600; }
.diag-legend-toggle { cursor: pointer; user-select: none; }
.table thead th { font-size: .82rem; border-top: none; }
</style>
